import { useQueries } from '@tanstack/react-query';
import { countBy, join, map, size, some } from 'lodash';

import { getInferenceCloudOptimizedSlides, InferenceCloudOptimizedSlidesResponse } from 'api/platform';
import { CasesParams } from 'utils/useCasesParams/types';

export enum ExecutionRequirementType {
  CloudOptimizedSlides = 'cloudOptimizedSlides',
}

export interface CloudOptimizedSlidesRequirementConfig {
  type: ExecutionRequirementType.CloudOptimizedSlides;
  label?: string;
  casesParams: CasesParams;
  slideStainType?: string | null;
  slideIds?: string[] | null;
  allowSelectedCohortFallback?: boolean;
  enabled?: boolean;
}

export type ExecutionRequirementConfig = CloudOptimizedSlidesRequirementConfig;

export interface CloudOptimizedSlidesRequirementState {
  type: ExecutionRequirementType.CloudOptimizedSlides;
  label: string;
  isApplicable: boolean;
  needsInput: boolean;
  isLoading: boolean;
  isError: boolean;
  isSatisfied: boolean;
  blocksExecution: boolean;
  availableVersionSummary: string;
  missingSlideIds: string[];
  data?: InferenceCloudOptimizedSlidesResponse;
}

export type ExecutionRequirementState = CloudOptimizedSlidesRequirementState;

export interface ExecutionRequirementsState {
  requirements: ExecutionRequirementState[];
  isLoading: boolean;
  hasBlockingFailures: boolean;
  canExecute: boolean;
}

const buildVersionSummary = (data?: InferenceCloudOptimizedSlidesResponse) => {
  const versionCounts = countBy(
    map(data?.slides || [], (slideStatus) => slideStatus.cloudOptimizationVersion).filter(Boolean)
  );

  return join(
    map(versionCounts, (count, version) => `${version} (${count} slide${count === 1 ? '' : 's'})`),
    ', '
  );
};

export const useExecutionRequirements = (
  requirements: ExecutionRequirementConfig[] = []
): ExecutionRequirementsState => {
  const requirementQueries = useQueries({
    queries: map(requirements, (requirement) => ({
      queryKey: [
        'executionRequirement',
        requirement.type,
        requirement.casesParams,
        requirement.slideStainType,
        requirement.slideIds,
        requirement.allowSelectedCohortFallback,
      ],
      queryFn: ({ signal }: { signal?: AbortSignal }) =>
        getInferenceCloudOptimizedSlides(
          {
            ...requirement.casesParams,
            configParams: {
              ...(requirement.slideStainType ? { slideStainType: requirement.slideStainType } : {}),
              ...(size(requirement.slideIds) > 0 ? { slideIds: requirement.slideIds as string[] } : {}),
            },
          },
          signal
        ),
      enabled:
        (requirement.enabled ?? true) &&
        Boolean(requirement.casesParams.studyId || requirement.casesParams.filters?.studyId) &&
        (Boolean(requirement.slideStainType) ||
          size(requirement.slideIds) > 0 ||
          Boolean(requirement.allowSelectedCohortFallback)),
      retry: false,
    })),
  });

  const resolvedRequirements = map(requirements, (requirement, index) => {
    const query = requirementQueries[index];
    const isApplicable = requirement.enabled ?? true;
    const hasExplicitSlideIds = size(requirement.slideIds) > 0;
    const needsInput =
      isApplicable && !requirement.allowSelectedCohortFallback && !requirement.slideStainType && !hasExplicitSlideIds;
    const data = query?.data as InferenceCloudOptimizedSlidesResponse | undefined;
    const isLoading = Boolean(query?.isLoading || query?.isFetching);
    const isError = Boolean(query?.isError);
    const isSatisfied = Boolean(data?.allSlidesHaveCloudOptimizedSlides);
    const missingSlideIds = data?.missingSlideIds || [];
    const blocksExecution = isApplicable && !needsInput && (isLoading || isError || !isSatisfied);

    return {
      type: requirement.type,
      label: requirement.label || 'Cloud-optimized slides',
      isApplicable,
      needsInput,
      isLoading,
      isError,
      isSatisfied,
      blocksExecution,
      availableVersionSummary: buildVersionSummary(data),
      missingSlideIds,
      data,
    } satisfies CloudOptimizedSlidesRequirementState;
  });

  return {
    requirements: resolvedRequirements,
    isLoading: some(resolvedRequirements, 'isLoading'),
    hasBlockingFailures: some(resolvedRequirements, 'blocksExecution'),
    canExecute:
      size(resolvedRequirements) === 0 ||
      !some(resolvedRequirements, (requirementState) => requirementState.blocksExecution),
  };
};
